import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { Navigate } from 'react-router'
import api from '../utils/api'
import toast from 'react-hot-toast'
import ReactMarkdown from 'react-markdown'
import { withLineBreaks } from '../utils/markdown'
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BellIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline'
import SendChoiceModal from '../components/SendChoiceModal'
import MeetupStatusBadge from '../components/MeetupStatusBadge'

const AdminMeetups = () => {
  const { user, isAdmin, loading: authLoading } = useAuth()
  const [meetups, setMeetups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingMeetup, setEditingMeetup] = useState(null)
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 })
  const [showStatsModal, setShowStatsModal] = useState(false)
  const [statsData, setStatsData] = useState(null)
  const [loadingStats, setLoadingStats] = useState(false)
  const [availableDates, setAvailableDates] = useState([])
  const [availableLocations, setAvailableLocations] = useState([])
  const [availableCreators, setAvailableCreators] = useState([])
  const [showFilters, setShowFilters] = useState(false)

  // Reminder customization state
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [reminderMeetupId, setReminderMeetupId] = useState(null)
  const [reminderSubject, setReminderSubject] = useState('')
  const [reminderContent, setReminderContent] = useState('')
  const [showMarkdownPreview, setShowMarkdownPreview] = useState(false)
  const [loadingReminder, setLoadingReminder] = useState(false)

  // Invitation customization state
  const [showInvitationModal, setShowInvitationModal] = useState(false)
  const [invitationMeetupId, setInvitationMeetupId] = useState(null)
  const [invitationSubject, setInvitationSubject] = useState('')
  const [invitationContent, setInvitationContent] = useState('')
  const [invitationSentAt, setInvitationSentAt] = useState(null)
  const [showInvitationPreview, setShowInvitationPreview] = useState(false)
  const [loadingInvitation, setLoadingInvitation] = useState(false)

  // Keuzestap bij versturen: null of { kind: 'invitation' | 'reminder', meetupId, counts }
  const [sendChoice, setSendChoice] = useState(null)
  const [sending, setSending] = useState(false)

  // Filter state
  const [filters, setFilters] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    created_by: ''
  })

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    max_participants: ''
  })

  useEffect(() => {
    const fetchAvailableDates = async () => {
      try {
        const response = await api.get('/admin/meetups/dates')
        setAvailableDates(response.data.dates || [])
      } catch (error) {
        console.error('Fetch dates error:', error)
      }
    }

    const fetchAvailableLocations = async () => {
      try {
        const response = await api.get('/admin/meetups/locations')
        setAvailableLocations(response.data.locations || [])
      } catch (error) {
        console.error('Fetch locations error:', error)
      }
    }

    const fetchAvailableCreators = async () => {
      try {
        const response = await api.get('/admin/meetups/creators')
        setAvailableCreators(response.data.creators || [])
      } catch (error) {
        console.error('Fetch creators error:', error)
      }
    }

    fetchAvailableDates()
    fetchAvailableLocations()
    fetchAvailableCreators()
  }, [])

  // Bumping reloadKey refetches after a change
  const [reloadKey, setReloadKey] = useState(0)
  const reload = () => setReloadKey(k => k + 1)

  useEffect(() => {
    const fetchMeetups = async () => {
      try {
        const params = new URLSearchParams({
          page: pagination.page,
          limit: pagination.limit,
          ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
        })
        const response = await api.get(`/admin/meetups?${params}`)
        setMeetups(response.data.meetups)
        setPagination(prev => ({
          ...prev,
          ...response.data.pagination
        }))
      } catch (error) {
        setError('Fout bij ophalen van meetups')
        console.error('Fetch meetups error:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMeetups()
  }, [pagination.page, pagination.limit, filters, reloadKey])

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: '',
      start_time: '',
      end_time: '',
      location: '',
      max_participants: ''
    })
    setEditingMeetup(null)
    setShowCreateForm(false)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
    setPagination(prev => ({ ...prev, page: 1 })) // Reset to first page when filtering
  }

  const clearFilters = () => {
    setFilters({
      title: '',
      description: '',
      date: '',
      location: '',
      created_by: ''
    })
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    try {
      setError('')
      await api.post('/meetups', formData)
      toast.success('Concept opgeslagen. Nog niet zichtbaar voor leden.')
      setSuccess('Concept opgeslagen. Nog niet zichtbaar voor leden.')
      resetForm()
      reload()
    } catch (error) {
      // Check if we have validation details
      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        const detailMessages = error.response.data.details
          .map(detail => `${detail.message}`)
          .join('\n')
        toast.error(detailMessages)
      } else {
        const errorMsg = error.response?.data?.error || 'Fout bij aanmaken van meetup'
        toast.error(errorMsg)
        setError(errorMsg)
      }
    }
  }

  const handleEdit = (meetup) => {
    setFormData({
      title: meetup.title,
      description: meetup.description,
      date: meetup.date,
      start_time: meetup.start_time,
      end_time: meetup.end_time,
      location: meetup.location,
      max_participants: meetup.max_participants || ''
    })
    setEditingMeetup(meetup)
    setShowCreateForm(true)
  }

  // Kopie als nieuwe meetup: de datum blijft leeg zodat je hem bewust kiest.
  // Aanmeldingen, mails en opgeslagen teksten horen bij het origineel.
  const handleCopy = (meetup) => {
    setFormData({
      title: meetup.title,
      description: meetup.description,
      date: '',
      start_time: meetup.start_time,
      end_time: meetup.end_time,
      location: meetup.location,
      max_participants: meetup.max_participants || ''
    })
    setEditingMeetup(null)
    setShowCreateForm(true)
  }

  const handleUpdate = async (e) => {
    e.preventDefault()
    try {
      setError('')
      await api.put(`/meetups/${editingMeetup.id}`, formData)
      toast.success('Meetup succesvol bijgewerkt!')
      setSuccess('Meetup succesvol bijgewerkt!')
      resetForm()
      reload()
    } catch (error) {
      // Check if we have validation details
      if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
        const detailMessages = error.response.data.details
          .map(detail => `${detail.message}`)
          .join('\n')
        toast.error(detailMessages)
      } else {
        const errorMsg = error.response?.data?.error || 'Fout bij bijwerken van meetup'
        toast.error(errorMsg)
        setError(errorMsg)
      }
    }
  }

  const handleDelete = async (meetupId) => {
    if (!window.confirm('Weet je zeker dat je deze meetup wilt verwijderen?')) {
      return
    }

    try {
      setError('')
      await api.delete(`/meetups/${meetupId}`)
      setSuccess('Meetup succesvol verwijderd!')
      reload()
    } catch (error) {
      setError(error.response?.data?.error || 'Fout bij verwijderen van meetup')
    }
  }

  const exportParticipants = async (meetupId) => {
    try {
      const response = await api.get(`/admin/meetups/${meetupId}/export`, {
        responseType: 'blob'
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `meetup-${meetupId}-deelnemers.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      setError('Fout bij exporteren van deelnemers')
    }
  }

  const viewStats = async (meetup) => {
    try {
      setLoadingStats(true)
      setShowStatsModal(true)
      const response = await api.get(`/admin/meetups/${meetup.id}/participants`)
      setStatsData({
        meetup,
        ...response.data
      })
    } catch (error) {
      toast.error('Fout bij ophalen van statistieken')
      setShowStatsModal(false)
    } finally {
      setLoadingStats(false)
    }
  }

  const closeStatsModal = () => {
    setShowStatsModal(false)
    setStatsData(null)
  }

  const isFutureMeetup = (meetup) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const meetupDate = new Date(meetup.date)
    meetupDate.setHours(0, 0, 0, 0)
    return meetupDate >= today
  }

  const openReminderModal = async (meetupId) => {
    setReminderMeetupId(meetupId)
    setLoadingReminder(true)

    try {
      // Laad opgeslagen template of default waarden
      const response = await api.get(`/admin/meetups/${meetupId}/reminder-template`)
      setReminderSubject(response.data.subject || '')
      setReminderContent(response.data.content || '')
      setShowReminderModal(true)
    } catch (error) {
      toast.error('Fout bij laden van template')
    } finally {
      setLoadingReminder(false)
    }
  }

  const saveReminderTemplate = async () => {
    if (!reminderSubject || !reminderContent) {
      toast.error('Subject en content zijn verplicht')
      return
    }

    try {
      await api.post(`/admin/meetups/${reminderMeetupId}/reminder-template`, {
        subject: reminderSubject,
        content: reminderContent
      })
      toast.success('Template opgeslagen')
    } catch (error) {
      toast.error('Fout bij opslaan van template')
    }
  }

  const openInvitationModal = async (meetupId) => {
    setInvitationMeetupId(meetupId)
    setShowInvitationPreview(false)
    setLoadingInvitation(true)

    try {
      const response = await api.get(`/admin/meetups/${meetupId}/invitation-template`)
      setInvitationSubject(response.data.subject || '')
      setInvitationContent(response.data.content || '')
      setInvitationSentAt(response.data.invitation_sent_at || null)
      setShowInvitationModal(true)
    } catch (error) {
      toast.error('Fout bij laden van uitnodiging')
    } finally {
      setLoadingInvitation(false)
    }
  }

  const saveInvitationTemplate = async () => {
    if (!invitationSubject || !invitationContent) {
      toast.error('Onderwerp en bericht zijn verplicht')
      return
    }

    try {
      await api.post(`/admin/meetups/${invitationMeetupId}/invitation-template`, {
        subject: invitationSubject,
        content: invitationContent
      })
      toast.success('Template opgeslagen')
    } catch (error) {
      toast.error('Fout bij opslaan van template')
    }
  }

  const openSendChoice = async (kind) => {
    const isInvitation = kind === 'invitation'
    const meetupId = isInvitation ? invitationMeetupId : reminderMeetupId
    const subject = isInvitation ? invitationSubject : reminderSubject
    const content = isInvitation ? invitationContent : reminderContent

    if (!subject || !content) {
      toast.error('Onderwerp en bericht zijn verplicht')
      return
    }

    // Houdt "Versturen…" uitgeschakeld tot de aantallen er zijn.
    const setLoading = isInvitation ? setLoadingInvitation : setLoadingReminder
    setLoading(true)

    try {
      const response = await api.get(`/admin/meetups/${meetupId}/recipient-counts`)
      setSendChoice({ kind, meetupId, counts: response.data })
    } catch (error) {
      toast.error('Fout bij ophalen van ontvangers')
    } finally {
      setLoading(false)
    }
  }

  const sendChoiceOptions = (choice) => {
    const { counts } = choice
    const testOption = { value: 'test', label: `Proefmail naar mij (${counts.admin_email})` }
    const allOption = { value: 'all', label: `Alle leden (${counts.all})`, disabled: counts.all === 0 }

    if (choice.kind === 'invitation') {
      return [testOption, allOption]
    }

    return [
      testOption,
      {
        value: 'not_responded',
        label: `Alleen leden die nog niet gereageerd hebben (${counts.not_responded})`,
        disabled: counts.not_responded === 0
      },
      allOption
    ]
  }

  const sendChosen = async (choice) => {
    const isInvitation = sendChoice.kind === 'invitation'
    const isTest = choice === 'test'
    const endpoint = isInvitation
      ? (isTest ? 'send-test-invitation' : 'send-invitation')
      : (isTest ? 'send-test-reminder' : 'send-reminder')
    const body = isInvitation
      ? { subject: invitationSubject, content: invitationContent }
      : { subject: reminderSubject, content: reminderContent }

    if (!isInvitation && !isTest) {
      body.send_to_all = choice === 'all'
    }

    setSending(true)

    try {
      const response = await api.post(`/admin/meetups/${sendChoice.meetupId}/${endpoint}`, body)
      toast.success(response.data.message)
      setSendChoice(null)

      // Na een proefmail blijft de modal open om aan te passen of echt te versturen.
      if (!isTest) {
        if (isInvitation) {
          setShowInvitationModal(false)
        } else {
          setShowReminderModal(false)
        }
        reload()
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fout bij versturen')
    } finally {
      setSending(false)
    }
  }

  const handlePublish = async (meetupId) => {
    if (!window.confirm('Meetup publiceren zonder e-mail te versturen?')) {
      return
    }

    try {
      await api.post(`/admin/meetups/${meetupId}/publish`)
      toast.success('Meetup gepubliceerd')
      reload()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fout bij publiceren')
    }
  }

  if (authLoading) {
    return <div className="flex justify-center items-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
    </div>
  }

  if (!isAdmin) {
    return <Navigate to="/login" replace />
  }

  return (
    <div className="container mx-auto px-4 py-8 min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Meetup Beheer</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Nieuwe Meetup
        </button>
      </div>

      {error && (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-sm mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-300 px-4 py-3 rounded-sm mb-4">
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
        >
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Filters</h2>
          {showFilters ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          )}
        </button>

        {showFilters && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-end mb-4">
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-500"
              >
                Wis filters
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Titel
                </label>
                <input
                  type="text"
                  name="title"
                  value={filters.title}
                  onChange={handleFilterChange}
                  placeholder="Zoek op titel..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Beschrijving
                </label>
                <input
                  type="text"
                  name="description"
                  value={filters.description}
                  onChange={handleFilterChange}
                  placeholder="Zoek op beschrijving..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Datum
                </label>
                <select
                  name="date"
                  value={filters.date}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Alle datums</option>
                  {availableDates.map((date) => (
                    <option key={date} value={date}>
                      {date}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Locatie
                </label>
                <select
                  name="location"
                  value={filters.location}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Alle locaties</option>
                  {availableLocations.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Aangemaakt door
                </label>
                <select
                  name="created_by"
                  value={filters.created_by}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Alle makers</option>
                  {availableCreators.map((creator) => (
                    <option key={creator} value={creator}>
                      {creator}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Modal */}
      {showStatsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Meetup Statistieken</h2>
              <button
                onClick={closeStatsModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>

            {loadingStats ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : statsData ? (
              <>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">{statsData.meetup.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {statsData.meetup.date} om {statsData.meetup.start_time}
                    {statsData.meetup.end_time && ` - ${statsData.meetup.end_time}`}
                  </p>
                </div>

                {/* Invitation Statistics */}
                <div className="mb-8">
                  <h4 className="text-md font-semibold mb-4 text-gray-700 dark:text-gray-300">Uitnodiging Overzicht</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 flex flex-col justify-between items-center text-center">
                      <div className="text-sm text-blue-600 font-medium mb-2">Verstuurd</div>
                      <div className="text-2xl font-bold text-blue-900">
                        {statsData.invitationStats.invitationsSent}
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4 flex flex-col justify-between items-center text-center">
                      <div className="text-sm text-green-600 font-medium mb-2">Ik kom</div>
                      <div className="text-2xl font-bold text-green-900">
                        {statsData.participants.confirmed.length}
                      </div>
                    </div>

                    <div className="bg-red-50 rounded-lg p-4 flex flex-col justify-between items-center text-center">
                      <div className="text-sm text-red-600 font-medium mb-2">Ik kom niet</div>
                      <div className="text-2xl font-bold text-red-900">
                        {statsData.participants.cancelled?.length || 0}
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4 flex flex-col justify-between items-center text-center">
                      <div className="text-sm text-purple-600 font-medium mb-2">Reactie percentage</div>
                      <div className="text-2xl font-bold text-purple-900">
                        {statsData.invitationStats.responseRate}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reminder Statistics */}
                {statsData.invitationStats.remindersSent > 0 && (
                  <div className="mb-8">
                    <h4 className="text-md font-semibold mb-4 text-gray-700 dark:text-gray-300">Herinneringsmail Statistieken</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="bg-amber-50 rounded-lg p-4 flex flex-col justify-between items-center text-center">
                        <div className="text-sm text-amber-600 font-medium mb-2">Herinneringen verstuurd</div>
                        <div className="text-2xl font-bold text-amber-900">
                          {statsData.invitationStats.remindersSent}
                        </div>
                      </div>

                      <div className="bg-teal-50 rounded-lg p-4 flex flex-col justify-between items-center text-center">
                        <div className="text-sm text-teal-600 font-medium mb-2">Reacties na herinnering</div>
                        <div className="text-2xl font-bold text-teal-900">
                          {statsData.invitationStats.respondedAfterReminder || 0}
                        </div>
                      </div>

                      <div className="bg-indigo-50 rounded-lg p-4 flex flex-col justify-between items-center text-center">
                        <div className="text-sm text-indigo-600 font-medium mb-2">Reactie % na herinnering</div>
                        <div className="text-2xl font-bold text-indigo-900">
                          {statsData.invitationStats.responseRateAfterReminder || 0}%
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Participants Lists */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-300">
                      Bevestigde Deelnemers ({statsData.participants.confirmed.length})
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
                      {statsData.participants.confirmed.length > 0 ? (
                        <ul className="space-y-2">
                          {statsData.participants.confirmed.map((participant) => (
                            <li key={participant.id} className="text-sm">
                              <div className="font-medium text-gray-900 dark:text-white">{participant.name}</div>
                              <div className="text-gray-500 dark:text-gray-400 text-xs">{participant.email}</div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">Nog geen bevestigde deelnemers</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-300">
                      Wachtlijst ({statsData.participants.waitlist.length})
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
                      {statsData.participants.waitlist.length > 0 ? (
                        <ul className="space-y-2">
                          {statsData.participants.waitlist.map((participant) => (
                            <li key={participant.id} className="text-sm">
                              <div className="font-medium text-gray-900 dark:text-white">{participant.name}</div>
                              <div className="text-gray-500 dark:text-gray-400 text-xs">{participant.email}</div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">Niemand op de wachtlijst</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-300">
                      Afgezegd ({statsData.participants.cancelled?.length || 0})
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
                      {statsData.participants.cancelled && statsData.participants.cancelled.length > 0 ? (
                        <ul className="space-y-2">
                          {statsData.participants.cancelled.map((participant) => (
                            <li key={participant.id} className="text-sm">
                              <div className="font-medium text-gray-900 dark:text-white">{participant.name}</div>
                              <div className="text-gray-500 dark:text-gray-400 text-xs">{participant.email}</div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400 text-sm">Geen afzeggingen</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={closeStatsModal}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    Sluiten
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
              {editingMeetup ? 'Meetup Bewerken' : 'Nieuwe Meetup'}
            </h2>

            <form onSubmit={editingMeetup ? handleUpdate : handleCreate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Titel
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Beschrijving
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Datum
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Locatie
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Starttijd
                  </label>
                  <input
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Eindtijd
                  </label>
                  <input
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Max Deelnemers (optioneel)
                  </label>
                  <input
                    type="number"
                    name="max_participants"
                    value={formData.max_participants}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingMeetup ? 'Bijwerken' : 'Aanmaken'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Meetups Table */}
      {loading ? (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {meetups.map((meetup) => (
              <div key={meetup.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 cursor-pointer" onClick={() => handleEdit(meetup)}>
                <div className="mb-3">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                    {meetup.title}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                    {meetup.description}
                  </p>
                  <div className="mt-2">
                    <MeetupStatusBadge meetup={meetup} />
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-start text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300 w-28 shrink-0">Datum & Tijd:</span>
                    <span className="text-gray-900 dark:text-white">
                      {meetup.date}<br />
                      {meetup.start_time}
                      {meetup.end_time && ` - ${meetup.end_time}`}
                    </span>
                  </div>
                  <div className="flex items-start text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300 w-28 shrink-0">Locatie:</span>
                    <span className="text-gray-900 dark:text-white">{meetup.location}</span>
                  </div>
                  <div className="flex items-start text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300 w-28 shrink-0">Deelnemers:</span>
                    <span className="text-gray-900 dark:text-white">
                      {meetup.registration_count}
                      {meetup.max_participants && ` / ${meetup.max_participants}`}
                    </span>
                  </div>
                  <div className="flex items-start text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300 w-28 shrink-0">Aangemaakt door:</span>
                    <span className="text-gray-900 dark:text-white">{meetup.created_by_name}</span>
                  </div>
                </div>

                <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700" onClick={(e) => e.stopPropagation()}>
                  {isFutureMeetup(meetup) && meetup.published && (
                    <button
                      onClick={() => openReminderModal(meetup.id)}
                      className="text-amber-600 hover:text-amber-900 p-2 hover:bg-amber-50 rounded-sm transition-colors"
                      title="Verstuur herinnering"
                    >
                      <BellIcon className="h-5 w-5" />
                    </button>
                  )}
                  {isFutureMeetup(meetup) && (
                    <button
                      onClick={() => openInvitationModal(meetup.id)}
                      className="text-indigo-600 hover:text-indigo-900 p-2 hover:bg-indigo-50 rounded-sm transition-colors"
                      title="Verstuur uitnodiging"
                    >
                      <EnvelopeIcon className="h-5 w-5" />
                    </button>
                  )}
                  {!meetup.published && (
                    <button
                      onClick={() => handlePublish(meetup.id)}
                      className="text-teal-600 hover:text-teal-900 p-2 hover:bg-teal-50 rounded-sm transition-colors"
                      title="Publiceren zonder mail"
                    >
                      <GlobeAltIcon className="h-5 w-5" />
                    </button>
                  )}
                  <button
                    onClick={() => viewStats(meetup)}
                    className="text-purple-600 hover:text-purple-900 p-2 hover:bg-purple-50 rounded-sm transition-colors"
                    title="Bekijk statistieken"
                  >
                    <ChartBarIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => exportParticipants(meetup.id)}
                    className="text-green-600 hover:text-green-900 p-2 hover:bg-green-50 rounded-sm transition-colors"
                    title="Export deelnemers"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleCopy(meetup)}
                    className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors"
                    title="Kopiëren als nieuwe meetup"
                  >
                    <DocumentDuplicateIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleEdit(meetup)}
                    className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-sm transition-colors"
                    title="Bewerken"
                  >
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(meetup.id)}
                    className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded-sm transition-colors"
                    title="Verwijderen"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Meetup
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Datum & Tijd
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Locatie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Deelnemers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Aangemaakt door
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Acties
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {meetups.map((meetup) => (
                  <tr key={meetup.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => handleEdit(meetup)}>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {meetup.title}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {meetup.description}
                        </div>
                        <div className="mt-2">
                          <MeetupStatusBadge meetup={meetup} />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{meetup.date}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {meetup.start_time}
                        {meetup.end_time && ` - ${meetup.end_time}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{meetup.location}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {meetup.registration_count}
                        {meetup.max_participants && ` / ${meetup.max_participants}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{meetup.created_by_name}</div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap justify-end gap-2">
                        {isFutureMeetup(meetup) && meetup.published && (
                          <button
                            onClick={() => openReminderModal(meetup.id)}
                            className="text-amber-600 hover:text-amber-900 p-1 hover:bg-amber-50 rounded-sm transition-colors"
                            title="Verstuur herinnering"
                          >
                            <BellIcon className="h-5 w-5" />
                          </button>
                        )}
                        {isFutureMeetup(meetup) && (
                          <button
                            onClick={() => openInvitationModal(meetup.id)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 hover:bg-indigo-50 rounded-sm transition-colors"
                            title="Verstuur uitnodiging"
                          >
                            <EnvelopeIcon className="h-5 w-5" />
                          </button>
                        )}
                        {!meetup.published && (
                          <button
                            onClick={() => handlePublish(meetup.id)}
                            className="text-teal-600 hover:text-teal-900 p-1 hover:bg-teal-50 rounded-sm transition-colors"
                            title="Publiceren zonder mail"
                          >
                            <GlobeAltIcon className="h-5 w-5" />
                          </button>
                        )}
                        <button
                          onClick={() => viewStats(meetup)}
                          className="text-purple-600 hover:text-purple-900 p-1 hover:bg-purple-50 rounded-sm transition-colors"
                          title="Bekijk statistieken"
                        >
                          <ChartBarIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => exportParticipants(meetup.id)}
                          className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded-sm transition-colors"
                          title="Export deelnemers"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleCopy(meetup)}
                          className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors"
                          title="Kopiëren als nieuwe meetup"
                        >
                          <DocumentDuplicateIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(meetup)}
                          className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded-sm transition-colors"
                          title="Bewerken"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(meetup.id)}
                          className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded-sm transition-colors"
                          title="Verwijderen"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 mt-4 lg:mt-0 rounded-lg lg:rounded-none shadow-sm lg:shadow-none">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page <= 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Vorige
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.pages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Volgende
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Pagina <span className="font-medium">{pagination.page}</span> van{' '}
                    <span className="font-medium">{pagination.pages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-xs -space-x-px">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page <= 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      Vorige
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= pagination.pages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      Volgende
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Reminder Customization Modal */}
      {showReminderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
              Herinnering Aanpassen
            </h2>

            <div className="space-y-6">
              {/* Subject Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Onderwerp
                </label>
                <input
                  type="text"
                  value={reminderSubject}
                  onChange={(e) => setReminderSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Herinnering: {{name}}"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Gebruik <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded-sm">{'{{name}}'}</code> voor de naam van de ontvanger
                </p>
              </div>

              {/* Content Field with Markdown Support */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bericht
                  </label>
                  <button
                    onClick={() => setShowMarkdownPreview(!showMarkdownPreview)}
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
                  >
                    {showMarkdownPreview ? 'Bewerken' : 'Preview'}
                  </button>
                </div>

                {showMarkdownPreview ? (
                  <div className="min-h-[300px] p-4 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 prose dark:prose-invert max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[]}
                      components={{
                        p: ({children}) => <p style={{marginBottom: '0.5em'}}>{children}</p>
                      }}
                    >
                      {withLineBreaks(reminderContent)}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <textarea
                    value={reminderContent}
                    onChange={(e) => setReminderContent(e.target.value)}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
                    placeholder="Beste {{name}},

Je bent uitgenodigd voor de meetup, maar we hebben nog geen reactie van je ontvangen.

**Gebruik Markdown** voor opmaak:
- **Vet** met **tekst**
- *Cursief* met *tekst*
- # Koppen met #

Groeten!"
                  />
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Ondersteunt Markdown voor opmaak. Gebruik <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded-sm">{'{{name}}'}</code> voor personalisatie.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex space-x-3">
                  <button
                    onClick={saveReminderTemplate}
                    disabled={loadingReminder}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                  >
                    Template Opslaan
                  </button>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowReminderModal(false)}
                    disabled={loadingReminder}
                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50 transition-colors"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={() => openSendChoice('reminder')}
                    disabled={loadingReminder}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    Versturen…
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invitation Modal */}
      {showInvitationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
              Uitnodiging Versturen
            </h2>

            <div className="space-y-6">
              {invitationSentAt && (
                <div className="px-4 py-3 rounded-md bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm">
                  Uitnodiging al verstuurd op {new Date(invitationSentAt.replace(' ', 'T') + 'Z').toLocaleString('nl-NL')}.
                  Opnieuw versturen betekent dat iedereen de mail nogmaals krijgt.
                </div>
              )}

              {/* Subject Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Onderwerp
                </label>
                <input
                  type="text"
                  value={invitationSubject}
                  onChange={(e) => setInvitationSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Nieuwe domoticaGrunn meetup"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Gebruik <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded-sm">{'{{name}}'}</code> voor de naam van de ontvanger
                </p>
              </div>

              {/* Content Field with Markdown Support */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bericht
                  </label>
                  <button
                    onClick={() => setShowInvitationPreview(!showInvitationPreview)}
                    className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
                  >
                    {showInvitationPreview ? 'Bewerken' : 'Preview'}
                  </button>
                </div>

                {showInvitationPreview ? (
                  <div className="min-h-[300px] p-4 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 prose dark:prose-invert max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[]}
                      components={{
                        p: ({children}) => <p style={{marginBottom: '0.5em'}}>{children}</p>
                      }}
                    >
                      {withLineBreaks(invitationContent)}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <textarea
                    value={invitationContent}
                    onChange={(e) => setInvitationContent(e.target.value)}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono text-sm"
                  />
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Ondersteunt Markdown voor opmaak. De knoppen &lsquo;Ik kom!&rsquo; en &lsquo;Ik kan niet&rsquo;, de agenda-link en de uitschrijflink worden automatisch toegevoegd.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex space-x-3">
                  <button
                    onClick={saveInvitationTemplate}
                    disabled={loadingInvitation}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                  >
                    Template Opslaan
                  </button>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowInvitationModal(false)}
                    disabled={loadingInvitation}
                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50 transition-colors"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={() => openSendChoice('invitation')}
                    disabled={loadingInvitation}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    Versturen…
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {sendChoice && (
        <SendChoiceModal
          title={sendChoice.kind === 'invitation' ? 'Uitnodiging versturen' : 'Herinnering versturen'}
          options={sendChoiceOptions(sendChoice)}
          hint={sendChoice.counts.published ? null : 'Links in de proefmail werken pas na publicatie.'}
          warning={sendChoice.kind === 'invitation' && invitationSentAt
            ? `Er is op ${new Date(invitationSentAt.replace(' ', 'T') + 'Z').toLocaleString('nl-NL')} al een uitnodiging verstuurd. Kies je ‘Alle leden’, dan krijgt iedereen de mail opnieuw.`
            : null}
          sending={sending}
          onSend={sendChosen}
          onCancel={() => setSendChoice(null)}
        />
      )}
    </div>
  )
}

export default AdminMeetups